import React, { useEffect, useState } from "react";
import { Form, Button, Table, Modal, Pagination, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

const AddModel = () => {
    // States for data
    const [cars, setCars] = useState([]);
    const [makers, setMakers] = useState([]);
    const [models, setModels] = useState([]);
    const [totalModels, setTotalModels] = useState(0);
    
    // Form states
    const [selectedCar, setSelectedCar] = useState("");
    const [selectedMaker, setSelectedMaker] = useState("");
    const [modelName, setModelName] = useState("");
    const [modelYear, setModelYear] = useState(""); // Text format for year
    const [editModelId, setEditModelId] = useState(null);
    
    // UI states
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [deleteModelId, setDeleteModelId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    
    // Fetch cars and models on component mount
    useEffect(() => {
        fetchCars();
        fetchModels(1);
    }, []);
    
    // Fetch makers when car selection changes
    useEffect(() => {
        if (selectedCar) {
            fetchMakers();
        }
    }, [selectedCar]);
    
    // Fetch cars
    const fetchCars = async () => {
        try {
            const response = await axios.get("https://motor-easy.clickbooster.io/api/cars");
            setCars(response.data);
        } catch (error) {
            console.error("Error fetching cars:", error);
            toast.error("Failed to load cars");
        }
    };
    
    // Fetch makers based on selected car
    const fetchMakers = async () => {
        try {
            const response = await axios.get(`https://motor-easy.clickbooster.io/api/maker?car_id=${selectedCar}`);
            setMakers(response.data.makers);
        } catch (error) {
            console.error("Error fetching makers:", error);
            toast.error("Failed to load makers");
        }
    };
    
    // Fetch models with pagination and search - FIXED SEARCH FUNCTIONALITY
    const fetchModels = async (page) => {
        setLoading(true);
        try {
            // Clean up search query and ensure it's properly encoded
            const encodedSearchQuery = encodeURIComponent(searchQuery.trim());
            
            console.log("Fetching models:", {
                page,
                limit: itemsPerPage,
                search: encodedSearchQuery
            });
            
            // Make sure search parameter is correctly passed to API
            const url = `https://motor-easy.clickbooster.io/api/models`;
            const response = await axios.get(url, {
                params: {
                    page,
                    limit: itemsPerPage,
                    search: encodedSearchQuery || undefined // Don't send empty strings
                }
            });
            
            console.log("API Response:", response.data);
            
            setModels(Array.isArray(response.data.models) ? response.data.models : []);
            setTotalModels(response.data.total);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching models:", error);
            toast.error("Failed to load models");
        } finally {
            setLoading(false);
        }
    };
    
    // Handle add/edit model submission
    const handleSubmitModel = async () => {
        console.log("Submit button clicked");
        
        // Enhanced validation with specific feedback
        if (!selectedCar) {
            toast.error("Please select a car.");
            return;
        }
        
        if (!selectedMaker) {
            toast.error("Please select a maker.");
            return;
        }
        
        if (!modelName) {
            toast.error("Please enter a model name.");
            return;
        }
        
        if (!modelYear) {
            toast.error("Please specify a year.");
            return;
        }
        
        // Store year as string to maintain exactly what user entered
        const yearText = modelYear.trim();
        
        // For validation purposes only, check if it's a reasonable year
        const yearValue = parseInt(yearText);
        if (isNaN(yearValue) || yearValue < 1900 || yearValue > new Date().getFullYear() + 1) {
            toast.error("Please enter a valid year (between 1900 and " + (new Date().getFullYear() + 1) + ")");
            return;
        }
        
        // Log the data being sent
        console.log("Submitting model with data:", {
            model_name: modelName,
            maker_id: selectedMaker,
            year: yearText // Send as text to API
        });
        
        setLoading(true);
        
        try {
            if (editModelId) {
                // Edit existing model
                const response = await axios.put(`https://motor-easy.clickbooster.io/api/models/${editModelId}`, {
                    model_name: modelName,
                    maker_id: selectedMaker,
                    year: yearText // Send as text
                });
                console.log("Update response:", response.data);
                toast.success("Model updated successfully!");
            } else {
                // Add new model
                const response = await axios.post("https://motor-easy.clickbooster.io/api/models", {
                    model_name: modelName,
                    maker_id: selectedMaker,
                    year: yearText // Send as text
                });
                console.log("Create response:", response.data);
                toast.success("Model added successfully!");
            }
            
            // Reset form and fetch updated models
            resetForm();
            fetchModels(currentPage);
            setShowModal(false);
        } catch (error) {
            console.error("Error details:", error.response ? error.response.data : error);
            
            // Enhanced error handling
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(`Error: ${error.response.data.message}`);
            } else {
                toast.error(editModelId ? "Error updating model." : "Error adding model.");
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Handle delete model
    const handleDeleteModel = async () => {
        if (!deleteModelId) return;
        
        setLoading(true);
        try {
            await axios.delete(`https://motor-easy.clickbooster.io/api/models/${deleteModelId}`);
            toast.success("Model deleted successfully!");
            
            // If we're on the last page and there's only one item, go back a page
            if (Array.isArray(models) && models.length === 1 && currentPage > 1) {
                fetchModels(currentPage - 1);
            } else {
                fetchModels(currentPage);
            }
            
        } catch (error) {
            toast.error("Error deleting model.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setDeleteModelId(null);
        }
    };
    
    // Open edit modal with model data
    const openEditModal = (model) => {
        setEditModelId(model.id);
        setSelectedCar(model.car_id);
        setSelectedMaker(model.maker_id);
        setModelName(model.model_name);
        // Ensure year is handled as text
        setModelYear(model.year ? String(model.year) : "");
        setShowModal(true);
        
        // Make sure makers are loaded for the car
        if (model.car_id) {
            fetchMakers();
        }
    };
    
    // Open add modal
    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };
    
    // Reset form fields
    const resetForm = () => {
        setEditModelId(null);
        setSelectedCar("");
        setSelectedMaker("");
        setModelName("");
        setModelYear(""); // Reset year field as text
    };
    
    // Handle search - FIXED SEARCH HANDLER
    const handleSearch = (e) => {
        e.preventDefault();
        fetchModels(1); // Reset to first page when searching
    };
    
    // Clear search
    const clearSearch = () => {
        setSearchQuery("");
        // Use setTimeout to ensure state is updated before fetching
        setTimeout(() => fetchModels(1), 0);
    };
    
    // Calculate pagination
    const totalPages = Math.ceil(totalModels / itemsPerPage);
    
    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        
        // Show first page and ellipsis if needed
        if (currentPage > 3) {
            items.push(
                <Pagination.Item key={1} onClick={() => fetchModels(1)}>
                    1
                </Pagination.Item>
            );
            items.push(<Pagination.Ellipsis key="ellipsis1" />);
        }
        
        // Show the pages around current page
        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => fetchModels(i)}>
                    {i}
                </Pagination.Item>
            );
        }
        
        // Show last page and ellipsis if needed
        if (currentPage < totalPages - 2) {
            items.push(<Pagination.Ellipsis key="ellipsis2" />);
            items.push(
                <Pagination.Item key={totalPages} onClick={() => fetchModels(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        return items;
    };
    
    return (
        <div className="model-management-container">
            <h2>Model Management</h2>
            
            {/* Search and Add New Button - IMPROVED SEARCH UI */}
            <div className="d-flex justify-content-between mb-3">
                <Form className="d-flex" onSubmit={handleSearch} style={{ width: '60%' }}>
                    <Form.Control
                        type="search"
                        placeholder="Search models..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="me-2"
                    />
                    <Button type="submit" variant="outline-primary" className="me-2">Search</Button>
                    {searchQuery && (
                        <Button variant="outline-secondary" onClick={clearSearch}>
                            Clear
                        </Button>
                    )}
                </Form>
                <Button variant="primary" onClick={openAddModal}>
                    Add New Model
                </Button>
            </div>
            
            {/* Search status indicator */}
            {searchQuery && (
                <div className="mb-3">
                    <span>
                        Showing results for: <strong>"{searchQuery}"</strong>
                        {models.length > 0 ? ` (${totalModels} results found)` : " (No results found)"}
                    </span>
                </div>
            )}
            
            {/* Models Table - Single Table Only */}
            {loading && models.length === 0 ? (
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Model Name</th>
                                <th>Year</th>
                                <th>Maker</th>
                                <th>Car</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {models.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center">No models found</td>
                                </tr>
                            ) : (
                                models.map((model, index) => (
                                    <tr key={model.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{model.model_name}</td>
                                        <td>{model.year}</td> {/* Display year as text */}
                                        <td>{model.maker_name}</td>
                                        <td>{model.car_name}</td>
                                        <td>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="me-2"
                                                onClick={() => openEditModal(model)}
                                            >
                                                Edit
                                            </Button>
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm"
                                                onClick={() => {
                                                    setDeleteModelId(model.id);
                                                    setShowDeleteModal(true);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.Prev 
                                    onClick={() => fetchModels(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                {getPaginationItems()}
                                <Pagination.Next 
                                    onClick={() => fetchModels(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            )}

            {/* Add/Edit Model Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editModelId ? "Edit Model" : "Add New Model"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="carSelect">
                            <Form.Label>Select Car</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedCar}
                                onChange={(e) => {
                                    setSelectedCar(e.target.value);
                                    setSelectedMaker(""); // Reset maker when car changes
                                }}
                            >
                                <option value="">-- Select a Car --</option>
                                {cars.map((car) => (
                                    <option key={car.id} value={car.id}>{car.car_name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="makerSelect">
                            <Form.Label>Select Maker</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedMaker}
                                onChange={(e) => setSelectedMaker(e.target.value)}
                                disabled={!selectedCar}
                            >
                                <option value="">-- Select a Maker --</option>
                                {makers.map((maker) => (
                                    <option key={maker.id} value={maker.id}>{maker.maker_name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="modelName">
                            <Form.Label>Model Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={modelName}
                                onChange={(e) => setModelName(e.target.value)}
                                placeholder="Enter model name"
                            />
                        </Form.Group>

                        {/* Year field as text input */}
                        <Form.Group className="mb-3" controlId="modelYear">
                            <Form.Label>Year</Form.Label>
                            <Form.Control
                                type="text" // Explicitly text, not number
                                value={modelYear}
                                onChange={(e) => setModelYear(e.target.value)}
                                placeholder="Enter model year (e.g., 2023)"
                            />
                            <Form.Text className="text-muted">
                                Please enter a single year (e.g., 2023), not a range.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSubmitModel} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Processing...</span>
                            </>
                        ) : (
                            editModelId ? "Update Model" : "Add Model"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this model? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteModel} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Deleting...</span>
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AddModel;